import GeneralDialog from '../../../../shared/general-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  deleteScheduledUserStatusUpdate,
  UserKeys,
} from '../../../../api/users-api';
import { useStore } from '../../../state/zustand-store';
import { Messages } from '../../../../utils/message-constants';
import { useState } from 'react';
import {
  isAbleToSeeUIElement,
  UserRoles,
} from '../../../../utils/roles/client-roles';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { ModuleList } from '../../../../shared/analytics/module-list';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { useAnalytics } from '../../../../shared/analytics/create-events';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface ScheduledUserStatusCancelDialogProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
  onCancelSuccess?: () => void;
  changeLogRoles: string[];
}

export function ScheduledUserStatusCancelDialog({
  open,
  onClose,
  userEmail,
  changeLogRoles,
}: ScheduledUserStatusCancelDialogProps) {
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { setSnackbar } = useStore();
  const { createEvent } = useAnalytics();

  const onCancel = () => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        UserRoles.ScheduleUserStatusUpdate.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteScheduledUserStatusUpdate(userEmail, changeLogMessage),
    onSuccess: () => {
      queryClient.setQueryData(
        [UserKeys.ScheduledUserStatusUpdate, userEmail],
        null
      );
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Cancelled(
          userEmail + "'s scheduled status update"
        ),
      });
      onClose();
    },
    onError: () => {
      setSnackbar({
        open: true,
        severity: 'error',
        message: Messages.Errors.Network.Generic,
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.cancel_scheduled_status,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.userDetails,
        }
      );
    },
  });

  return (
    <GeneralDialog
      open={open}
      handleClose={onClose}
      title="Cancel Scheduled Status Change"
      dialogContent={
        <>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate();
            }}
            mutateLoading={isPending}
          />
          <Stack flexDirection={'column'} spacing={2}>
            <Typography>
              Do you wish to cancel the scheduled status change?
            </Typography>
          </Stack>
        </>
      }
      primaryButton={{
        action: () => onCancel(),
        disabled: isPending,
        loading: isPending,
        title: 'CANCEL STATUS CHANGE',
      }}
    />
  );
}

export default ScheduledUserStatusCancelDialog;
